import { useEffect, useState } from 'react';

/**
 * An 8Ball that drops and bounces wherever the page is tapped or clicked.
 *
 * Pure decoration, and treated as such: it listens on the document rather than
 * wrapping anything, never sits in front of a control, and can be deleted
 * without touching a line of the app. Nothing on a match night depends on it.
 *
 * Bound to `pointerdown`, not `click`, so it fires on a phone the moment a
 * finger lands — the app is used on phones, and an effect only desktop can see
 * would be an effect almost nobody sees.
 */

interface Ball {
  id: number;
  x: number;
  y: number;
  /** Sideways travel over the bounce, so no two land the same way. */
  drift: number;
  /** Degrees of spin, either direction. */
  spin: number;
  size: number;
}

/**
 * Where a bounce would be unwelcome.
 *
 * Score boxes and dropdowns are the one place taps are work rather than play:
 * a scorer entering sixteen numbers at 21:00 does not want a ball for each. The
 * install prompt is excluded because it is already a small target over the tab
 * bar.
 */
const NO_BOUNCE = 'input, select, textarea, option, .install';

// Math.random is right here, unlike the coin toss: nothing is being decided.
const between = (min: number, max: number) => min + Math.random() * (max - min);

export default function BounceBall() {
  const [balls, setBalls] = useState<Ball[]>([]);

  useEffect(() => {
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (reduced?.matches) return;

    let next = 0;
    const onDown = (event: PointerEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest?.(NO_BOUNCE)) return;

      const ball: Ball = {
        id: (next += 1),
        x: event.clientX,
        y: event.clientY,
        drift: between(-26, 26),
        spin: between(-200, 200),
        size: between(26, 38),
      };
      // Capped, so a child drumming on the screen cannot fill the page with
      // balls faster than they animate away.
      setBalls((current) => [...current.slice(-11), ball]);
    };

    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, []);

  const done = (id: number) => setBalls((current) => current.filter((b) => b.id !== id));

  if (balls.length === 0) return null;

  return (
    <div className="bounce-layer" aria-hidden="true">
      {balls.map((ball) => (
        <span
          key={ball.id}
          className="bounce-drift"
          style={
            {
              left: ball.x,
              top: ball.y,
              width: ball.size,
              height: ball.size,
              marginLeft: -ball.size / 2,
              marginTop: -ball.size / 2,
              '--drift': `${ball.drift}px`,
              '--spin': `${ball.spin}deg`,
            } as React.CSSProperties
          }
          onAnimationEnd={() => done(ball.id)}
        >
          {/* Two elements, two jobs: the outer carries the sideways drift and
              the fade, the inner the fall, the squash and the spin. Authoring
              both on one element would mean repeating the whole transform in
              every keyframe. */}
          <img className="bounce-ball" src="/brand/8ball-coin.webp" alt="" />
        </span>
      ))}
    </div>
  );
}
